import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, ShieldOff, Shield, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-line screening summary block.
 *
 * Source of truth: `GET /admin/merchants/{mid}/status-overview` (Brief D v1
 * rollup endpoint). Designed to be embedded anywhere — merchant detail page,
 * Brief D dashboard when that ships, etc.
 *
 * Props:
 *   merchantId — required.
 *   className  — optional outer class.
 */
export default function ScreeningHeadline({ merchantId, className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/admin/merchants/${merchantId}/status-overview`,
          { credentials: "include" },
        );
        if (!r.ok) throw new Error(`${r.status}`);
        const body = await r.json();
        if (!cancelled) setData(body);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  if (loading) {
    return (
      <div className={`text-[12px] text-zinc-500 ${className}`} data-testid="screening-headline-loading">
        Loading screening status…
      </div>
    );
  }
  if (!data) return null;
  const s = data.screening || {};
  const tone = s.tone || "neutral";
  const palette = {
    ok: { ring: "border-emerald-400/30 bg-emerald-400/[0.06]",
          fg: "text-emerald-300", Icon: ShieldCheck },
    warn: { ring: "border-amber-400/30 bg-amber-400/[0.06]",
            fg: "text-amber-300", Icon: ShieldAlert },
    skipped: { ring: "border-zinc-500/30 bg-zinc-500/[0.06]",
               fg: "text-zinc-300", Icon: ShieldOff },
    neutral: { ring: "border-white/[0.08] bg-white/[0.02]",
               fg: "text-zinc-400", Icon: Shield },
  }[tone] || { ring: "border-white/[0.08]", fg: "text-zinc-400", Icon: Shield };
  const Icon = palette.Icon;

  return (
    <div
      data-testid={`screening-headline-${s.kind}`}
      className={`rounded-xl border ${palette.ring} px-3 py-2 inline-flex items-center gap-2 ${className}`}
    >
      <Icon className={`h-3.5 w-3.5 ${palette.fg}`} />
      <span className={`text-[12.5px] ${palette.fg}`}>{s.headline}</span>
      <Link
        to={s.cross_check_url || `/admin/merchants/${merchantId}/cross-check`}
        data-testid="screening-headline-cta"
        className="text-[11.5px] text-zinc-500 hover:text-white inline-flex items-center gap-1"
      >
        View <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
}
